// Re-compilation trigger
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
    Bell, Plus, Trash2, Edit, CheckCircle,
    AlertCircle, Info, AlertTriangle, ToggleLeft as Toggle,
    RefreshCw, X
} from 'lucide-react';
import API_BASE_URL from '../../config/api';
import { useAlert } from '../../context/AlertContext';
import { useConfirm } from '../../context/ConfirmContext';

const NoticeManagement = () => {
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingNotice, setEditingNotice] = useState(null);
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        type: 'info',
        status: 'active'
    });

    const fetchNotices = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/notices.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setNotices(data.data);
            }
        } catch (err) {
            showAlert('Failed to fetch notices', 'error');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchNotices();
    }, [fetchNotices]);

    const handleOpenModal = (notice = null) => {
        if (notice) {
            setEditingNotice(notice);
            setFormData({
                title: notice.title,
                content: notice.content,
                type: notice.type,
                status: notice.status
            });
        } else {
            setEditingNotice(null);
            setFormData({ title: '', content: '', type: 'info', status: 'active' });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const action = editingNotice ? 'update' : 'add';
        const payload = {
            ...formData,
            action,
            id: editingNotice?.id
        };

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/notices.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                showAlert(data.message, 'success');
                setIsModalOpen(false);
                fetchNotices();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (err) {
            showAlert('Operation failed', 'error');
        }
    };

    const handleDelete = (id) => {
        showConfirm({
            title: 'Delete Notice',
            message: 'Are you sure you want to delete this notice?',
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('authToken');
                    await fetch(`${API_BASE_URL}/admin/notices.php`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ action: 'delete', id })
                    });
                    showAlert('Notice deleted', 'success');
                    fetchNotices();
                } catch (err) {
                    showAlert('Delete failed', 'error');
                }
            }
        });
    };

    const toggleStatus = async (id) => {
        try {
            const token = localStorage.getItem('authToken');
            await fetch(`${API_BASE_URL}/admin/notices.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'toggle', id })
            });
            fetchNotices();
        } catch (err) {
            showAlert('Toggle failed', 'error');
        }
    };

    const getTypeStyles = (type) => {
        switch (type) {
            case 'danger': return 'bg-red-50 text-red-700 border-red-100';
            case 'warning': return 'bg-amber-50 text-amber-700 border-amber-100';
            case 'success': return 'bg-green-50 text-green-700 border-green-100';
            default: return 'bg-blue-50 text-blue-700 border-blue-100';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'danger': return <AlertCircle size={18} />;
            case 'warning': return <AlertTriangle size={18} />;
            case 'success': return <CheckCircle size={18} />;
            default: return <Info size={18} />;
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Notice Board Management</h1>
                        <p className="text-gray-500 text-sm mt-1">Create and manage public announcements for users</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 font-bold"
                    >
                        <Plus size={20} /> Add New Notice
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <RefreshCw className="animate-spin text-indigo-600" size={32} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {notices.map((notice) => (
                            <div key={notice.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                                <div className={`p-4 border-b flex items-center justify-between ${getTypeStyles(notice.type)}`}>
                                    <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs">
                                        {getTypeIcon(notice.type)}
                                        {notice.type}
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleOpenModal(notice)} className="p-1 hover:bg-white/50 rounded transition-colors">
                                            <Edit size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(notice.id)} className="p-1 hover:bg-white/50 rounded transition-colors text-red-600">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="p-5 flex-1">
                                    <h3 className="font-bold text-gray-900 text-lg mb-2">{notice.title}</h3>
                                    <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">{notice.content}</p>
                                </div>
                                <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between mt-auto">
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{new Date(notice.created_at).toLocaleDateString()}</span>
                                    <button
                                        onClick={() => toggleStatus(notice.id)}
                                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all ${notice.status === 'active'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-200 text-gray-700'
                                            }`}
                                    >
                                        <Toggle size={14} className={notice.status === 'active' ? 'rotate-180 transition-transform' : ''} />
                                        {notice.status === 'active' ? 'Active' : 'Inactive'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-indigo-50/50">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white rounded-xl shadow-sm text-indigo-600">
                                        <Bell size={24} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {editingNotice ? 'Edit Notice' : 'New Announcement'}
                                    </h3>
                                </div>
                                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-white rounded-full text-gray-400">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-8 space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Notice Title</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all"
                                        placeholder="Enter notice title"
                                        value={formData.title}
                                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Category / Level</label>
                                        <select
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                                            value={formData.type}
                                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                                        >
                                            <option value="info">Info (Blue)</option>
                                            <option value="success">Success (Green)</option>
                                            <option value="warning">Warning (Yellow)</option>
                                            <option value="danger">Danger (Red)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Initial Status</label>
                                        <select
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                                            value={formData.status}
                                            onChange={e => setFormData({ ...formData, status: e.target.value })}
                                        >
                                            <option value="active">Active (Visible)</option>
                                            <option value="inactive">Inactive (Hidden)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Detailed Content</label>
                                    <textarea
                                        required
                                        rows="5"
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all"
                                        placeholder="Write notice details here..."
                                        value={formData.content}
                                        onChange={e => setFormData({ ...formData, content: e.target.value })}
                                    />
                                </div>

                                <div className="pt-4 flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 px-4 py-4 border border-gray-200 text-gray-700 font-bold rounded-2xl hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all flex items-center justify-center gap-2"
                                    >
                                        {editingNotice ? 'Update Notice' : 'Post Announcement'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default NoticeManagement;
